/**
 * Job Detail Page
 * 
 * Detailed view of a single GPU job.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Cpu,
  Clock,
  DollarSign,
  Server,
  Activity,
  Trash2,
  RefreshCw,
  Copy,
  CheckCircle,
  XCircle,
  Loader2,
  Globe,
  Lock,
  Key,
  Download,
  Terminal,
  Monitor,
  Pause,
} from 'lucide-react';
import { gpuJobsApi, GpuJob } from '../lib/api';
import { format, formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import WebTerminal from '../components/WebTerminal';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showTerminal, setShowTerminal] = useState(false);

  const { data: job, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['gpu-job', jobId],
    queryFn: () => gpuJobsApi.get(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) =>
      query.state.data?.status && ['running', 'provisioning', 'pending'].includes(query.state.data.status)
        ? 5000
        : false,
  });

  const cancelMutation = useMutation({
    mutationFn: gpuJobsApi.cancel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gpu-job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      toast.success('Job cancelled successfully');
    },
    onError: () => {
      toast.error('Failed to cancel job');
    },
  });

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const downloadSshKey = (privateKey: string, keyName: string) => {
    const blob = new Blob([privateKey], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${keyName}.pem`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('SSH key downloaded! Set permissions with: chmod 400 ' + keyName + '.pem');
  };

  const getSshCommand = (publicIp: string, keyName: string) => {
    return `ssh -i ${keyName}.pem ec2-user@${publicIp}`;
  };

  const getStatusBadge = (status: GpuJob['status']) => {
    const config = {
      pending: { class: 'badge-warning', icon: Clock },
      provisioning: { class: 'badge-info', icon: Loader2 },
      running: { class: 'badge-success', icon: Activity },
      stopping: { class: 'badge-warning', icon: Loader2 },
      stopped: { class: 'badge-warning', icon: Pause },
      completed: { class: 'badge-neutral', icon: CheckCircle },
      failed: { class: 'badge-error', icon: XCircle },
      cancelled: { class: 'badge-neutral', icon: XCircle },
      terminated: { class: 'badge-neutral', icon: XCircle },
    };
    return config[status] || config.pending;
  };

  const canCancel = (status: GpuJob['status']) =>
    ['pending', 'provisioning', 'running'].includes(status);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-gpu-400" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-24">
        <XCircle className="w-16 h-16 text-surface-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-300">Job not found</h2>
        <button onClick={() => navigate('/jobs')} className="btn-primary mt-4">
          Back to Jobs
        </button>
      </div>
    );
  }

  const statusConfig = getStatusBadge(job.status);
  const StatusIcon = statusConfig.icon;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-4xl mx-auto space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <button onClick={() => navigate('/jobs')} className="btn-ghost p-2 flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <h1 className="text-lg sm:text-2xl font-bold text-surface-100 font-mono">
                {job.id.slice(0, 8)}...
              </h1>
              <span className={clsx(statusConfig.class, 'flex items-center gap-1')}>
                <StatusIcon className={clsx('w-3 h-3', job.status === 'provisioning' && 'animate-spin')} />
                {job.status}
              </span>
            </div>
            <p className="text-surface-400 mt-1">
              Created {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="btn-secondary"
          >
            <RefreshCw className={clsx('w-4 h-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
          {canCancel(job.status) && (
            <button
              onClick={() => {
                if (confirm('Are you sure you want to cancel this job?')) {
                  cancelMutation.mutate(job.id);
                }
              }}
              disabled={cancelMutation.isPending}
              className="btn-danger"
            >
              <Trash2 className="w-4 h-4" />
              Cancel Job
            </button>
          )}
        </div>
      </motion.div>

      {/* Error Message */}
      {job.errorMessage && (
        <motion.div
          variants={itemVariants}
          className="p-4 rounded-lg bg-red-500/10 border border-red-500/30"
        >
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-400">Error</p>
              <p className="text-sm text-red-300 mt-1">{job.errorMessage}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Info Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Instance Details */}
        <motion.div variants={itemVariants} className="card">
          <h2 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Cpu className="w-5 h-5 text-gpu-400" />
            Instance Details
          </h2>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-surface-400">Type</span>
              <span className="font-mono text-surface-100">{job.instanceType}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-surface-400">GPUs</span>
              <span className="text-surface-100">{job.gpuCount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-surface-400">Pricing</span>
              <span className={clsx(job.useSpot ? 'text-green-400' : 'text-surface-100')}>
                {job.useSpot ? 'Spot Instance' : 'On-Demand'}
              </span>
            </div>
            {job.instanceId && (
              <div className="flex justify-between items-center">
                <span className="text-surface-400">Instance ID</span>
                <button
                  onClick={() => copyToClipboard(job.instanceId!, 'Instance ID')}
                  className="flex items-center gap-2 text-surface-100 hover:text-gpu-400 font-mono text-sm"
                >
                  {job.instanceId}
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Cost & Runtime */}
        <motion.div variants={itemVariants} className="card">
          <h2 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-gpu-400" />
            Cost & Runtime
          </h2>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-surface-400">Estimated Cost</span>
              <span className="text-2xl font-bold text-surface-100">
                ${job.estimatedCostUsd.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-surface-400">Max Runtime</span>
              <span className="text-surface-100">{job.maxRuntimeHours} hours</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-surface-400">Environment</span>
              <span className="text-surface-100 capitalize">{job.environment}</span>
            </div>
            {job.project && (
              <div className="flex justify-between items-center">
                <span className="text-surface-400">Project</span>
                <span className="text-surface-100">{job.project}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Network */}
        {(job.privateIpAddress || job.publicIpAddress) && (
          <motion.div variants={itemVariants} className="card">
            <h2 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <Server className="w-5 h-5 text-gpu-400" />
              Network
            </h2>

            <div className="space-y-4">
              {job.publicIpAddress && (
                <div className="flex justify-between items-center">
                  <span className="text-surface-400 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Public IP
                  </span>
                  <button
                    onClick={() => copyToClipboard(job.publicIpAddress!, 'Public IP')}
                    className="flex items-center gap-2 text-surface-100 hover:text-gpu-400 font-mono"
                  >
                    {job.publicIpAddress}
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              )}
              {job.privateIpAddress && (
                <div className="flex justify-between items-center">
                  <span className="text-surface-400 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Private IP
                  </span>
                  <button
                    onClick={() => copyToClipboard(job.privateIpAddress!, 'Private IP')}
                    className="flex items-center gap-2 text-surface-100 hover:text-gpu-400 font-mono"
                  >
                    {job.privateIpAddress}
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* SSH Access */}
        {job.status === 'running' && job.publicIpAddress && (
          <motion.div variants={itemVariants} className="card md:col-span-2">
            <h2 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-gpu-400" />
              SSH Access
            </h2>

            {job.sshPrivateKey ? (
              <div className="space-y-4">
                {/* Web Terminal - One-Click SSH */}
                <div className="p-4 rounded-lg bg-gpu-500/10 border border-gpu-500/30">
                  <div className="flex items-start gap-3">
                    <Monitor className="w-5 h-5 text-gpu-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-gpu-300 mb-2">Web Terminal (Recommended)</p>
                      <p className="text-sm text-surface-400 mb-3">
                        Connect instantly via browser - no key download needed!
                      </p>
                      <button
                        onClick={() => setShowTerminal(!showTerminal)}
                        className="btn-primary"
                      >
                        <Terminal className="w-4 h-4" />
                        {showTerminal ? 'Hide Terminal' : 'Open Terminal'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Embedded Terminal */}
                {showTerminal && (
                  <div className="mt-4">
                    <WebTerminal 
                      jobId={job.id} 
                      onClose={() => setShowTerminal(false)} 
                    />
                  </div>
                )}

                {/* Manual SSH Option */}
                <div className="p-4 rounded-lg bg-surface-800/50 border border-surface-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-surface-300 font-medium">Manual SSH (Alternative)</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copyToClipboard(getSshCommand(job.publicIpAddress!, job.sshKeyName!), 'SSH command')}
                        className="btn-secondary text-sm py-1 px-3"
                      >
                        <Copy className="w-3 h-3" />
                        Copy Command
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-surface-900 rounded-md border border-surface-700">
                    <Terminal className="w-4 h-4 text-gpu-400 flex-shrink-0" />
                    <code className="text-sm text-gpu-300 font-mono break-all">
                      {getSshCommand(job.publicIpAddress!, job.sshKeyName!)}
                    </code>
                  </div>
                </div>

                <details className="group">
                  <summary className="cursor-pointer text-sm text-surface-400 hover:text-surface-300 flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Download SSH Key (for external tools)
                  </summary>
                  <div className="mt-3 pl-6 space-y-3">
                    <button
                      onClick={() => downloadSshKey(job.sshPrivateKey!, job.sshKeyName!)}
                      className="btn-secondary text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Download {job.sshKeyName}.pem
                    </button>
                    <div className="text-xs text-surface-500 space-y-1">
                      <p>• After downloading, run: <code className="text-surface-400">chmod 400 {job.sshKeyName}.pem</code></p>
                      <p>• Then connect with the SSH command above</p>
                    </div>
                  </div>
                </details>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Web Terminal still works without stored key */}
                <div className="p-4 rounded-lg bg-surface-800/50 border border-surface-700">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-surface-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-surface-300">SSH Key Not Available for Download</p>
                      <p className="text-sm text-surface-500 mt-1">
                        The SSH private key is only shown immediately after job creation. 
                        You can still use the Web Terminal if the backend has the key cached.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Timeline */}
        <motion.div variants={itemVariants} className="card">
          <h2 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gpu-400" />
            Timeline
          </h2>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-surface-400">Created</span>
              <span className="text-surface-100">
                {format(new Date(job.createdAt), 'PPpp')}
              </span>
            </div>
            {job.startedAt && (
              <div className="flex justify-between items-center">
                <span className="text-surface-400">Started</span>
                <span className="text-surface-100">
                  {format(new Date(job.startedAt), 'PPpp')}
                </span>
              </div>
            )}
            {job.completedAt && (
              <div className="flex justify-between items-center">
                <span className="text-surface-400">Completed</span>
                <span className="text-surface-100">
                  {format(new Date(job.completedAt), 'PPpp')}
                </span>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Full Job ID */}
      <motion.div variants={itemVariants} className="card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-surface-400 mb-1">Full Job ID</p>
            <p className="font-mono text-surface-200">{job.id}</p>
          </div>
          <button
            onClick={() => copyToClipboard(job.id, 'Job ID')}
            className="btn-ghost"
          >
            <Copy className="w-4 h-4" />
            Copy
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
